import { z } from 'zod';
import { ItemIdSchema } from './items';

/**
 * Attacks and weapons as data (COMBAT-ATTACK): the shapes the config defines and the catalog
 * clients get in their initial state, so they pace their own attacks and draw anyone's.
 */

/** How a projectile looks; travels as its index in this list (`bolt` events). */
export const PROJECTILE_LOOKS = ['orb', 'arrow', 'web', 'bolt'] as const;
export const ProjectileLookSchema = z.enum(PROJECTILE_LOOKS);
export type ProjectileLook = z.infer<typeof ProjectileLookSchema>;

/** Attack and weapon family ids: lower snake case. */
export const AttackIdSchema = z.string().regex(/^[a-z][a-z0-9_]{1,31}$/);
export const WeaponFamilyIdSchema = z.string().regex(/^[a-z][a-z0-9_]{1,31}$/);

/**
 * Where an attack reaches: an `arc` around the attacker (resolved against targets rewound to
 * what the attacker saw), or a `projectile` flying straight (resolved where it is, no rewind).
 */
export const AttackShapeSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('arc'),
    range: z.number().positive().max(5),
    arcDegrees: z.number().positive().max(360),
  }),
  z.object({
    kind: z.literal('projectile'),
    speed: z.number().positive().max(40),
    range: z.number().positive().max(40),
    radius: z.number().positive().max(2),
    look: ProjectileLookSchema,
  }),
]);
export type AttackShape = z.infer<typeof AttackShapeSchema>;

/** What a client knows of an attack: its shape and its timing. */
export const AttackViewSchema = z.object({
  id: AttackIdSchema,
  shape: AttackShapeSchema,
  windupMs: z.number().int().nonnegative(),
  recoveryMs: z.number().int().positive(),
  /**
   * How its wind-up shows besides its wedge: `quake`, the ground shakes under its giver, drawn by
   * the ground it stands on. Only on the attacks that have one.
   */
  tell: z.enum(['quake']).optional(),
  /**
   * What its hit does besides damage (COMBAT-CC), for drawing it by its effect: a stun, a push,
   * or a state (its index in the catalog's states). Only on the attacks that have one.
   */
  control: z
    .object({
      kind: z.enum(['stun', 'knockback', 'state']),
      state: z.number().int().nonnegative().optional(),
    })
    .optional(),
});
export type AttackView = z.infer<typeof AttackViewSchema>;

/**
 * How a weapon's attack charges (COMBAT-ATTACK): a tap before `tapMs` is weak (`tapFactor`); held,
 * it grows to `maxFactor` at `maxMs`, where it goes off by itself; walking at `pace` meanwhile.
 */
export const ChargeViewSchema = z.object({
  tapMs: z.number().int().nonnegative(),
  tapFactor: z.number().positive(),
  maxMs: z.number().int().positive(),
  maxFactor: z.number().positive(),
  speedFactor: z.number().positive(),
  pace: z.number().positive(),
  /**
   * Held at its top (UD-1, B030): up to `maxMs` more, spending `staminaPerSecond` (times the
   * armor's factor) while it is; it goes off by itself then, or as the stamina it had runs out.
   */
  hold: z
    .object({ maxMs: z.number().int().nonnegative(), staminaPerSecond: z.number().positive() })
    .optional(),
});
export type ChargeView = z.infer<typeof ChargeViewSchema>;

/** A weapon's secondary action (COMBAT-DEFENSE), as clients pace and predict it. */
export const SecondaryViewSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('guard'),
    arcDegrees: z.number().positive(),
    pace: z.number().positive(),
    /** The parry's window (B019), for its user's prediction of what it may do next. */
    parryMs: z.number().int().positive(),
    /** How much later a parried attacker recovers (its own client paces it). */
    parryPenaltyMs: z.number().int().nonnegative(),
    /** A guard lowered sooner than this without parrying (a tap) leaves its user unable to act… */
    parryTapMs: z.number().int().nonnegative(),
    /** …this long. */
    parryMissMs: z.number().int().nonnegative(),
    /** A new window opens only this long after the last one opened, unless that one parried. */
    parryRearmMs: z.number().int().nonnegative(),
  }),
]);
export type SecondaryView = z.infer<typeof SecondaryViewSchema>;

/**
 * The last index of the catalog's `hands` (B115): a hand travels in one byte, 0 for nothing and
 * the index plus one otherwise.
 */
export const MAX_HAND_INDEX = 254;

/**
 * The combat catalog of the initial state: every attack (its index in `attacks` is how events
 * name it), every weapon family's attacks by index, every weapon item's family and quality, and
 * the family of bare hands.
 */
/** A weapon family as clients predict it: its chain, heavy blow, charge and secondary. */
export const FamilyViewSchema = z.object({
  /** Its chain of blows (B047), by attack index, and the window to keep it going. */
  chain: z.array(z.number().int().nonnegative()).min(1).max(4),
  chainMs: z.number().int().nonnegative(),
  /** What each step hits times (NC-2); absent: 1 each. */
  chainFactors: z.array(z.number().positive()).optional(),
  /** Its wielder's pace through a melee blow, from its start until ready again; absent: 1. */
  swingPace: z.number().min(0).max(1).optional(),
  /** Its heavy blow: the one a charge lets go, or its finisher (NC-2). */
  heavy: z.number().int().nonnegative(),
  /** How it charges (NC-1: only the bow). */
  charge: ChargeViewSchema.optional(),
  /**
   * Its finisher (NC-2, NC-3), every family that does not charge: the chain's last step, or the next
   * blow after a dodge or a just parry; it hits `factor` times and winds up `windupMs` more.
   */
  finisher: z
    .object({
      factor: z.number().positive(),
      windupMs: z.number().int().nonnegative(),
      /** How long a just dodge or parry primes it; absent: `chainMs`. */
      primeMs: z.number().int().nonnegative().optional(),
    })
    .optional(),
  secondary: SecondaryViewSchema.optional(),
  /** What each of its shots spends from the quiver (ITEM-AMMO); absent: nothing. */
  ammo: ItemIdSchema.optional(),
});
export type FamilyView = z.infer<typeof FamilyViewSchema>;

export const CombatCatalogSchema = z.object({
  attacks: z.array(AttackViewSchema),
  families: z.record(WeaponFamilyIdSchema, FamilyViewSchema),
  /** Each weapon object: its family, its quality and, for a variant, its own heavy blow (B046). */
  weapons: z.record(
    ItemIdSchema,
    z.object({
      family: WeaponFamilyIdSchema,
      quality: z.number(),
      heavy: z.number().int().nonnegative().optional(),
    }),
  ),
  bareHands: WeaponFamilyIdSchema,
  /**
   * What each object of the other hand gives that its wearer's client predicts (B040): a shield's
   * guard, which replaces the weapon's secondary (SE-1).
   */
  offhands: z.record(ItemIdSchema, z.object({ guard: SecondaryViewSchema.optional() })),
  /**
   * Every object a hand may hold, in the order `hands` and `gear` name them by (B115): what
   * others hold is drawn from here.
   */
  hands: z.array(ItemIdSchema).max(MAX_HAND_INDEX + 1),
  /**
   * The states (COMBAT-STATUS): their index in this list is how events name them; each with its
   * family (boon or bane) and its sign (R-15).
   */
  states: z.array(
    z.object({
      id: z.string(),
      family: z.enum(['boon', 'bane']),
      color: z.string().regex(/^#[0-9a-f]{6}$/),
      /** The radius in metres of the light it gives its carrier (a torch, ADR 0050). */
      light: z.number().positive().optional(),
      /** How far its carrier sees, in metres (Blinded): the server sends nothing beyond. */
      sight: z.number().positive().optional(),
      /**
       * Its carrier is hidden (CREATURE-AMBUSH) and drawn by halves as it perches: a hedge that
       * moves on the `ground`, a shadow `above`. Never a body in plain sight.
       */
      concealed: z.enum(['ground', 'above']).optional(),
      /** What its carrier cannot do while it lasts (Bruma, Sopor): its client does not try. */
      block: z.array(z.enum(['attack', 'technique', 'guard', 'dodge'])).optional(),
      /** Focus charges its carrier's spells cost less (Círculo), never below one: shown costs. */
      focusLess: z.number().int().positive().optional(),
    }),
  ),
});
export type CombatCatalog = z.infer<typeof CombatCatalogSchema>;
